import "chartjs-adapter-date-fns";

import {
  CategoryScale,
  Chart as ChartJS,
  defaults,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { Locale } from "date-fns";

import newTheme from "@/new_theme";
import { CHART_COLORS, getCSSVar } from "@/utils";
import { themeChange } from "@/utils/events";

import ThemePlugin from "./plugins/ThemePlugin";

export const displayFormats = {
  hour: "HH:mm",
  day: "yyyy-MM-dd",
  week: "w",
  quarter: "qqq",
  month: "MMMM",
  year: "yyyy-MMMM",
};

// Register plugins, controllers and graphs
ChartJS.register(
  Legend,
  LineController,
  CategoryScale,
  LineElement,
  Tooltip,
  PointElement,
  LinearScale,
  Title,
  ThemePlugin
);

// Setup defaults
defaults.layout.padding = {
  left: 0,
  right: 0,
  top: 20,
  bottom: 0,
};

defaults.clip = false;

defaults.responsive = true;
defaults.maintainAspectRatio = false;

defaults.onHover = (e, elements) => {
  // areas with data always has a hover.
  if (e.native?.target) {
    (e.native?.target as HTMLElement).style.cursor = elements.length
      ? "pointer"
      : "default";
  }
};

defaults.scales.category.border = {
  ...(defaults.scales.category.border || {}),
  display: false,
};

defaults.scales.time = defaults.scales.time || {};
defaults.scales.timeseries = defaults.scales.timeseries || {};

defaults.scales.time.time.tooltipFormat = "PP";
defaults.scales.time.time.displayFormats = displayFormats;

defaults.scales.timeseries.time.tooltipFormat = "PP";
defaults.scales.timeseries.time.displayFormats = displayFormats;

// Legend
defaults.plugins.legend.labels.usePointStyle = true;
defaults.plugins.legend.labels.boxWidth = 8;
defaults.plugins.legend.labels.boxHeight = 8;
defaults.plugins.legend.labels.borderRadius = 100;

defaults.plugins.legend.position = "bottom";
defaults.font.family = newTheme.fonts.body;
defaults.font.weight = 300;

// Tooltip
defaults.plugins.tooltip.enabled = true;
defaults.plugins.tooltip.mode = "index";
defaults.plugins.tooltip.usePointStyle = true;
defaults.plugins.tooltip.boxPadding = 8;

// Line
defaults.datasets.line.pointHitRadius = 20;
defaults.datasets.line.pointBorderWidth = 0;
defaults.datasets.line.pointHoverRadius = 5;
defaults.datasets.line.pointRadius = 3.5;
defaults.datasets.line.borderWidth = 2;

// Pie
ChartJS.overrides.doughnut.plugins.legend.position = "right";
ChartJS.overrides.doughnut.plugins.legend.align = "center";
ChartJS.overrides.doughnut.plugins.legend.labels.textAlign = "left";
ChartJS.overrides.doughnut.plugins.legend.fullSize = true;
ChartJS.overrides.doughnut.plugins.legend.labels.usePointStyle = true;

defaults.datasets.doughnut.backgroundColor = CHART_COLORS;
defaults.datasets.doughnut.borderWidth = 0;

ChartJS.overrides.doughnut.responsive = true;
ChartJS.overrides.doughnut.cutout = "80%";

export const setupChartTheme = () => {
  const scope = "default";

  const gridColor = getCSSVar("--chart-grid-color", scope);
  const textColor = getCSSVar("--chart-text-color", scope);

  ChartJS.overrides.doughnut.plugins.legend.labels.color = textColor;
  defaults.scale.grid.color = gridColor;
  defaults.scale.ticks.color = textColor;
  defaults.plugins.legend.labels.color = textColor;

  // Make sure we trigger the event so that all the charts have a cahnce to updatae
  document.dispatchEvent(themeChange);
};

// populate defaults
setupChartTheme();

export const setChartJsLocale = (locale: Locale) => {
  const adapters = {
    date: {
      locale,
    },
  };

  defaults.scales.time.adapters = adapters;
  defaults.scales.timeseries.adapters = adapters;
};

// Re-export used Graph types
export { Line as ChartJsLine } from "react-chartjs-2";
export { Bar as ChartJsBar } from "react-chartjs-2";
export { Doughnut as ChartJsDoughnut } from "react-chartjs-2";

export const hideLegend = {
  plugins: {
    legend: {
      display: false,
    },
  },
};

// Export the defaults so that we can access them where needed
export { defaults as ChartJsDefaults };
